import React from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Chip,
    CircularProgress,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DoNotDisturbAltRoundedIcon from '@mui/icons-material/DoNotDisturbAltRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded';

const SCREEN_READER_ONLY_SX = {
    position: 'absolute',
    width: 1,
    height: 1,
    p: 0,
    m: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

const STATUS_ICON_BY_KIND = {
    success: CheckCircleRoundedIcon,
    error: ErrorRoundedIcon,
    cancelled: CancelRoundedIcon,
    warning: ErrorRoundedIcon,
    pending: ScheduleRoundedIcon,
    info: ScheduleRoundedIcon,
    // A finished outcome worth reading, neither success nor failure.
    notice: InfoRoundedIcon,
};

export const historyLedgerRowSx = ({ current = false, last = false } = {}) => ({
    py: 0.65,
    px: current ? 1 : 0,
    minHeight: 42,
    position: 'relative',
    borderRadius: 1.25,
    bgcolor: current
        ? (theme) => alpha(theme.palette.primary.main, 0.045)
        : 'transparent',
    borderBottom: last ? 'none' : '1px solid',
    borderColor: 'divider',
    '&::before': current ? {
        content: '""',
        position: 'absolute',
        inset: '6px auto 6px 0',
        width: 2,
        borderRadius: 99,
        bgcolor: 'primary.main',
        boxShadow: (theme) => `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
    } : null,
});

export function HistoryStatusIcon({
    label,
    tooltip = '',
    kind = 'info',
    color = 'text.secondary',
    loading = false,
}) {
    const StatusIcon = STATUS_ICON_BY_KIND[kind] || ScheduleRoundedIcon;
    return (
        <Tooltip title={tooltip || label} arrow>
            <Box
                component="span"
                role="status"
                aria-label={label}
                sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    flex: '0 0 auto',
                    color,
                }}
            >
                {loading ? (
                    <CircularProgress size={15} thickness={5.5} color="inherit" />
                ) : (
                    <StatusIcon sx={{ fontSize: 17 }} />
                )}
                <Typography component="span" sx={SCREEN_READER_ONLY_SX}>
                    {label}
                </Typography>
            </Box>
        </Tooltip>
    );
}

HistoryStatusIcon.propTypes = {
    label: PropTypes.string.isRequired,
    tooltip: PropTypes.string,
    kind: PropTypes.oneOf(Object.keys(STATUS_ICON_BY_KIND)),
    color: PropTypes.string,
    loading: PropTypes.bool,
};

export function HistoryCategoryChip({ label, tooltip = '', color = 'primary', sx = {} }) {
    const chip = (
        <Chip
            size="small"
            variant="outlined"
            label={label}
            sx={[
                (theme) => {
                    const paletteColor = theme.palette[color]?.main || theme.palette.text.secondary;
                    return {
                        height: 22,
                        borderRadius: '7px',
                        borderColor: alpha(paletteColor, theme.palette.mode === 'dark' ? 0.46 : 0.3),
                        color: paletteColor,
                        background: `linear-gradient(135deg, ${alpha(paletteColor, theme.palette.mode === 'dark' ? 0.18 : 0.1)}, ${alpha(paletteColor, theme.palette.mode === 'dark' ? 0.07 : 0.025)})`,
                        boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.08 : 0.55)}`,
                        '& .MuiChip-label': {
                            px: 0.8,
                            fontSize: '0.64rem',
                            lineHeight: 1,
                            fontWeight: 700,
                            letterSpacing: '0.01em',
                        },
                    };
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        />
    );
    return tooltip ? <Tooltip title={tooltip} arrow>{chip}</Tooltip> : chip;
}

HistoryCategoryChip.propTypes = {
    label: PropTypes.string.isRequired,
    tooltip: PropTypes.string,
    color: PropTypes.string,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.func, PropTypes.array]),
};

export function HistoryMetric({ label, ariaLabel = label, value, color, symbol = '', icon = null }) {
    const count = Number(value);
    if (!Number.isFinite(count) || count <= 0) return null;
    return (
        <Tooltip title={label} arrow>
            <Box
                component="span"
                display="inline-flex"
                alignItems="center"
                gap={0.22}
                aria-label={ariaLabel}
                sx={{ color, whiteSpace: 'nowrap' }}
            >
                {icon}
                <Typography
                    component="span"
                    variant="caption"
                    sx={{
                        color: icon ? 'text.secondary' : 'inherit',
                        fontSize: '0.65rem',
                        fontWeight: 750,
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {icon ? count : `${symbol} ${count}`}
                </Typography>
            </Box>
        </Tooltip>
    );
}

HistoryMetric.propTypes = {
    label: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string.isRequired,
    symbol: PropTypes.string,
    icon: PropTypes.node,
};

export function HistorySummaryMetrics({
    added = 0,
    removed = 0,
    changed = 0,
    excluded = 0,
    itemLabel = 'securities',
}) {
    return (
        <Box
            component="span"
            display="inline-flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={0.9}
            sx={{ minWidth: 0 }}
        >
            <HistoryMetric
                label={`Added ${added} ${itemLabel}`}
                ariaLabel={`Added ${added}`}
                value={added}
                color="success.main"
                symbol="+"
            />
            <HistoryMetric
                label={`Removed ${removed} ${itemLabel}`}
                ariaLabel={`Removed ${removed}`}
                value={removed}
                color="error.main"
                symbol="−"
            />
            <HistoryMetric
                label={`Changed ${changed} ${itemLabel}`}
                ariaLabel={`Changed ${changed}`}
                value={changed}
                color="warning.main"
                icon={<SyncAltRoundedIcon sx={{ fontSize: 13 }} />}
            />
            <HistoryMetric
                label={`Excluded ${excluded} ${itemLabel}`}
                ariaLabel={`Excluded ${excluded}`}
                value={excluded}
                color="warning.main"
                icon={<DoNotDisturbAltRoundedIcon sx={{ fontSize: 13 }} />}
            />
        </Box>
    );
}

HistorySummaryMetrics.propTypes = {
    added: PropTypes.number,
    removed: PropTypes.number,
    changed: PropTypes.number,
    excluded: PropTypes.number,
    itemLabel: PropTypes.string,
};
